import { useEffect, useState } from "react";
import { Wrapper } from "nystem-components";
import app from "nystem";

const formatDiff = (value) => (value > 0 ? `+${value}` : value);
const formatPercent = ({ diff, first }) => {
  if (first === 0) return diff === 0 ? "0%" : "+100%";

  const percent = Math.round((diff / first) * 100);
  return `${percent > 0 ? "+" : ""}${percent}%`;
};

const diffClass = (value) =>
  value > 0 ? "text-green-400" : value < 0 ? "text-red-400" : "text-gray-400";

const numberClass = ["w-24", "whitespace-nowrap", "text-right"];
const changeClass = ["w-32", "whitespace-nowrap", "text-right"];

const withDelta = ({ first, id, last }) => ({
  diff: last - first,
  first,
  id,
  last,
});

const missingReference = (id) => `--- ${id} ---`;
const nameFrom = (refs, id) => refs[id]?.name || missingReference(id);
const nameFor = (refs, source, id) => nameFrom(refs[source], id);
const sortByName = (refs) => (a, b) =>
  nameFrom(refs, a.id).localeCompare(nameFrom(refs, b.id));

const getGroup = (data, id) => data.find((group) => group.id === id)?.rows || [];

const DeltaView = ({ model, view }) => {
  const [data, setData] = useState([]);
  const [refs, setRefs] = useState({ product: {}, program: {} });

  useEffect(() => {
    const onData = ({ data }) => setData(data || []);
    view.on(model.event || "firstLast", onData);

    return () => {
      view.off(model.event || "firstLast", onData);
    };
  }, [model.event, view]);

  const productRows = getGroup(data, "product").map(withDelta);
  const programRows = getGroup(data, "program").map(withDelta);
  const refData = JSON.stringify({
    product: productRows.map(({ id }) => id),
    program: programRows.map(({ id }) => id),
  });

  useEffect(() => {
    let mounted = true;
    const load = async (source, rows) => {
      const entries = await Promise.all(
        rows.map(({ id }) =>
          app.database[source]
            .get({ id })
            .then(({ data }) => [id, data || {}]),
        ),
      );
      return Object.fromEntries(entries);
    };

    Promise.all([
      load("product", productRows),
      load("program", programRows),
    ]).then(([product, program]) => {
      if (mounted) setRefs({ product, program });
    });

    return () => {
      mounted = false;
    };
  }, [refData]);

  if (!data.length) return null;

  return (
    <Wrapper
      renderAs="table"
      className={["table", "table-striped", "table-condensed", "w-full"]}
    >
      <Wrapper renderAs="thead">
        <Wrapper renderAs="tr">
          <Wrapper renderAs="th" />
          <Wrapper renderAs="th" className={numberClass}>
            {app.t("From")}
          </Wrapper>
          <Wrapper renderAs="th" className={numberClass}>
            {app.t("To")}
          </Wrapper>
          <Wrapper renderAs="th" className={changeClass}>
            {app.t("Change")}
          </Wrapper>
        </Wrapper>
      </Wrapper>
      <Wrapper renderAs="tbody">
        {programRows.sort(sortByName(refs.program)).map((program) => {
          const products = productRows
            .filter(({ id }) => {
              const value = refs.product[id]?.program || [];
              const programs = value instanceof Array ? value : [value];
              return programs.includes(program.id);
            })
            .sort(sortByName(refs.product));

          return [
            <Wrapper renderAs="tr" key={program.id}>
              <Wrapper renderAs="td" className={["font-semibold"]}>
                {nameFor(refs, "program", program.id)}
              </Wrapper>
              <Wrapper renderAs="td" className={numberClass}>
                {program.first}
              </Wrapper>
              <Wrapper renderAs="td" className={numberClass}>
                {program.last}
              </Wrapper>
              <Wrapper
                renderAs="td"
                className={[
                  ...changeClass,
                  "font-semibold",
                  diffClass(program.diff),
                ]}
              >
                {formatDiff(program.diff)} ({formatPercent(program)})
              </Wrapper>
            </Wrapper>,
            ...products.map((product) => (
              <Wrapper renderAs="tr" key={`${program.id}-${product.id}`}>
                <Wrapper renderAs="td" className={["pl-6"]}>
                  {nameFor(refs, "product", product.id)}
                </Wrapper>
                <Wrapper renderAs="td" className={numberClass}>
                  {product.first}
                </Wrapper>
                <Wrapper renderAs="td" className={numberClass}>
                  {product.last}
                </Wrapper>
                <Wrapper
                  renderAs="td"
                  className={[
                    ...changeClass,
                    "font-semibold",
                    diffClass(product.diff),
                  ]}
                >
                  {formatDiff(product.diff)} ({formatPercent(product)})
                </Wrapper>
              </Wrapper>
            )),
          ];
        })}
      </Wrapper>
    </Wrapper>
  );
};

export default DeltaView;
